import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter, map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-classify',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    TranslateModule,
  ],
  styles: [
    `
      :host {
        height: 100vh;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .classify-header {
        flex-shrink: 0;
      }

      :host ::ng-deep router-outlet + * {
        flex: 1;
        min-height: 0;
      }
    `,
  ],
  template: `
    @if (showListHeader()) {
      <div class="classify-header px-4 pt-4">
        <div class="p-2 flex-table-header">
          <h2 class="text-2xl font-semibold text-gray-800">
            {{ 'goods.classify.management.title' | translate }}
          </h2>
        </div>

        <div class="mt-4 flex-table-header">
          <div class="border-b border-gray-200">
            <nav class="-mb-px flex space-x-8">
              <a
                routerLink="/goods/classify"
                [routerLinkActiveOptions]="{ exact: true }"
                routerLinkActive
                #categoriesTab="routerLinkActive"
                [class]="
                  categoriesTab.isActive
                    ? 'border-blue-500 text-blue-600 border-b-2 py-2 px-1 text-sm font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 py-2 px-1 text-sm font-medium'
                "
              >
                {{ 'goods.classify.tabs.categories' | translate }}
              </a>
              <a
                routerLink="/goods/classify/drafts"
                routerLinkActive
                #draftsTab="routerLinkActive"
                [routerLinkActiveOptions]="{ exact: true }"
                [class]="
                  draftsTab.isActive
                    ? 'border-blue-500 text-blue-600 border-b-2 py-2 px-1 text-sm font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 py-2 px-1 text-sm font-medium'
                "
              >
                {{ 'goods.classify.tabs.drafts' | translate }}
              </a>
            </nav>
          </div>
        </div>
      </div>
    }
    <router-outlet></router-outlet>
  `,
})
export class ClassifyComponent {
  private readonly router = inject(Router);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /** Only list tabs need the parent title + tab bar; add/edit/view/draft detail use their own page title. */
  protected readonly showListHeader = computed(() => {
    const path = (this.currentUrl() || '').split('?')[0];
    return path === '/goods/classify' || path === '/goods/classify/drafts';
  });
}
